import { useState } from "react";
import { X } from "lucide-react";
import userApi from "../../../../api/userApi";
import { useNotification } from "../../../../hooks/useNotification";

export default function TransferPermissionModal({
  isOpen,
  setIsOpen,
  staffs,
  onSuccess,
}) {
  const { addNotification } = useNotification();
  const [fromStaffId, setFromStaffId] = useState("");
  const [toStaffId, setToStaffId] = useState("");
  const [isKeepOldStaff, setIsKeepOldStaff] = useState(false);
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    setIsOpen(false);
    setFromStaffId("");
    setToStaffId("");
    setIsKeepOldStaff(false);
    setOnlyUnread(false);
  };

  const handleSave = async () => {
    if (!fromStaffId || !toStaffId) {
      addNotification("Vui lòng chọn đầy đủ nhân viên", 0);
      return;
    }

    if (fromStaffId === toStaffId) {
      addNotification("Nhân viên nguồn và đích phải khác nhau", 0);
      return;
    }

    try {
      setIsLoading(true);
      const res = await userApi.transferCustomers({
        fromStaffId,
        toStaffId,
        isKeepOldStaff,
        onlyUnread,
      });

      if (res.status === 1) {
        addNotification(
          `Chuyển quyền thành công: ${res.data?.updatedCount || 0} khách hàng`,
          1,
        );
        if (onSuccess) onSuccess();
        handleClose();
      } else {
        addNotification(res.message || "Có lỗi xảy ra", -1);
      }
    } catch (error) {
      addNotification("Lỗi kết nối server", -1);
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const getStaffRoleName = (staff) => {
    if (staff.role === "super_admin") return "Super Admin";
    if (staff.role === "admin") return "Quản trị viên";
    return "Nhân viên";
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold">Chuyển quyền quản lý</h2>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex flex-col gap-6">
          <div className="grid grid-cols-[1fr_2fr] items-center gap-4">
            <label className="font-medium">Chọn nhân viên cần chuyển:</label>
            <select
              className="w-full border rounded-md px-3 py-2 outline-none focus:border-blue-500"
              value={fromStaffId}
              onChange={(e) => setFromStaffId(e.target.value)}
            >
              <option value="">-- Chọn nhân viên --</option>
              {staffs
                .filter((s) => !s.isDelete)
                .map((staff) => (
                  <option key={staff._id} value={staff._id}>
                    {staff.name} ({getStaffRoleName(staff)})
                  </option>
                ))}
            </select>
          </div>

          <div className="grid grid-cols-[1fr_2fr] items-center gap-4">
            <label className="font-medium">Chuyển sang cho:</label>
            <select
              className="w-full border rounded-md px-3 py-2 outline-none focus:border-blue-500"
              value={toStaffId}
              onChange={(e) => setToStaffId(e.target.value)}
            >
              <option value="">-- Chọn nhân viên --</option>
              {staffs
                .filter((s) => !s.isDelete)
                .map((staff) => (
                  <option key={staff._id} value={staff._id}>
                    {staff.name} ({getStaffRoleName(staff)})
                  </option>
                ))}
            </select>
          </div>

          <div className="flex flex-col gap-3 mt-2">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="w-5 h-5 mt-0.5"
                checked={isKeepOldStaff}
                onChange={(e) => setIsKeepOldStaff(e.target.checked)}
              />
              <span className="text-gray-700">
                Thêm nhân viên mới vào quản lý (giữ nhân viên cũ)
              </span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="w-5 h-5"
                checked={onlyUnread}
                onChange={(e) => setOnlyUnread(e.target.checked)}
              />
              <span className="text-gray-700">
                Chỉ chuyển những cuộc hội thoại chưa được đọc
              </span>
            </label>
          </div>

          <div className="flex justify-end gap-3 mt-4">
            <button
              onClick={handleClose}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Đóng
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {isLoading ? "Đang xử lý..." : "Chuyển quyền"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
